import { useState, useEffect } from 'react';

import { Tabs } from '@/components/Tabs';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import AccountSecurity from '@/pages/common/settingTabs/AccountSecurity';
import LanguageRegion from '@/pages/common/settingTabs/LanguageRegion';

import Preferences from './components/Preferences';
import ProfileSettings from './components/ProfileSettings';

type TSettingsTab = 'profile' | 'preferences' | 'language' | 'security';

const useSettingsTabs = () => {
  const { formatText } = useFormatText();

  return [
    {
      label: formatText('settings.tabs.profile-settings'),
      value: 'profile' as const,
    },
    {
      label: formatText('settings.tabs.preferences'),
      value: 'preferences' as const,
    },
    {
      label: formatText('settings.tabs.language-region'),
      value: 'language' as const,
    },
    {
      label: formatText('settings.tabs.account-security'),
      value: 'security' as const,
    },
  ];
};

const TAB_CONTENT: Record<TSettingsTab, React.FC> = {
  profile: ProfileSettings,
  preferences: Preferences,
  language: LanguageRegion,
  security: AccountSecurity,
};

const Settings = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();

  useEffect(() => {
    setTitle(formatText('settings.page-title'));

    return () => {
      setTitle('');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeTab, setActiveTab] = useState<TSettingsTab>('profile');
  const tabOptions = useSettingsTabs();
  const ActiveComponent = TAB_CONTENT[activeTab];

  return (
    <div
      className="flex flex-col gap-6 py-6"
      data-testid="teacher-settings-page"
    >
      {/* Tabs */}
      <Tabs options={tabOptions} value={activeTab} onChange={setActiveTab} />

      {/* Tab Content */}
      <div
        className="mt-2 mx-auto w-full flex items-center justify-center"
        data-testid="settings-tab-content"
      >
        <ActiveComponent />
      </div>
    </div>
  );
};

export default Settings;
